/**
 * De omlijsting van het spelscherm, op de plekken van het origineel.
 *
 * Alles hier rekent in het raster van 1024×768 waarop de cd tekende; `board.ts` schaalt
 * het geheel naar het venster. Coördinaten komen uit `~/catan-ref/shots/st2.png`, een
 * schermafdruk van het origineel onder Wine.
 *
 * ```
 *        [ statusbalk tegenstander ]           [portret]
 *                                              [naam][●][●]
 *  ═══════════════ rivier ═══════════════
 *  [9 ]                                             [handstapel]
 *  [10]              HET BORD
 *  [12]
 *  [13]                                             [handstapel]
 *  [11]  ← uitbouwstapels
 *        [ statusbalk jij ] [Volgende][✕]      [portret]
 * ```
 */
import { LANDSCAPE_RESOURCE, type Seat } from '@catan/contracts';
import type { GameView } from '@catan/rules';
import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { asset, hasAsset, klik } from './assets.js';
import { KOSTEN_ICOON, kostenRijen } from './kosten.js';
import {
  arcering,
  knop,
  negenStuk,
  RASTER_BREED,
  RASTER_HOOG,
  skinSprite,
  tekst,
  SKIN,
} from './skin.js';
import { Tooltips, volgTooltip } from './tooltip.js';

/**
 * De tien vakjes van de statusbalk, in de volgorde van het originele plaatje.
 *
 * Er bestaat ook een balk met elf vakjes (`MENU_074`), maar die heeft er één extra voor
 * toverkracht — en die bestaat alleen in Tovenaars & Draken. Het basisspel gebruikt de
 * balk met tien, en dat is ook wat op de schermafdruk van het origineel staat.
 *
 * Het laatste symbool oogt als gekruiste lansen maar is de **molen**: de wieken vormen
 * een kruis. Dat is de speelfiguur voor de grootste handelsmacht.
 */
export const BALK_SYMBOLEN = [
  'wol', 'erts', 'hout', 'goud', 'graan', 'klei',
  'toernooi', 'riddermacht', 'overwinning', 'handelsmacht',
] as const;

/** Wat elk vakje van de statusbalk betekent, in de bewoordingen van het handboek. */
const BALK_UITLEG: Readonly<Record<string, string>> = {
  wol: 'Wol',
  erts: 'Erts',
  hout: 'Hout',
  goud: 'Goud',
  graan: 'Graan',
  klei: 'Klei',
  toernooi: 'Toernooipunten — beslissen het riddertoernooi',
  riddermacht: 'Riddermacht — de speelfiguur Ridder',
  overwinning: 'Overwinningspunten',
  handelsmacht: 'Handelsmacht — de speelfiguur Molen',
};

/**
 * Het portret voor de menselijke speler, als er niets gekozen is.
 *
 * Je kiest je eigen figuur in het opzetscherm, en die keuze staat los van je naam — in het
 * origineel houdt `player.cfg` `#id_player` en de spelersnaam apart bij. Blijft de keuze
 * uit (een oude opslag, of het ontwikkelscherm), dan staat hier iemand, want in het
 * origineel is die hoek nooit leeg.
 */
const SPELER_PORTRET = 'louis';

/** Waar elk vakje begint, opgemeten in `MENU_077`. */
const VAK_X = [10, 55, 100, 145, 190, 235, 300, 345, 390, 435] as const;

/** Waar de onderdelen staan, gemeten op de schermafdruk van het origineel. */
export const PLEK = {
  // De balk is 660 breed maar de vakjes eindigen bij 475; we centreren de vakjes,
  // niet de plaat — dan staat hij waar hij in het origineel stond.
  balkBoven: { x: 270, y: 4 },
  balkOnder: { x: 175, y: 724 },
  berichten: { x: 182, y: 48 },
  portretBoven: { x: 916, y: 2 },
  naamplaatBoven: { x: 878, y: 104 },
  portretOnder: { x: 916, y: 640 },
  naamplaatOnder: { x: 878, y: 712 },
  handstapelBoven: { x: 973, y: 168 },
  handstapelOnder: { x: 973, y: 470 },
  stapels: { x: 6, y: 368, stap: 40 },
  // De ruilknop staat aan de linkerrand, boven de stapels — opgemeten op
  // `catan-ref/shots/mij/handelsaanbod-afgewezen.png`: 40×99 vanaf x 0, y 130.
  ruilknop: { x: 0, y: 130 },
  // De vier bouwstapels staan **linksonder**, onder de uitbouwstapels — niet erboven,
  // zoals ik eerst gokte. Opgemeten in `catan-ref/shots/mij/`: vakjes van 36×37 met een
  // lichte rand, x vanaf 0, y vanaf 571, stap 40. Volgorde van boven naar beneden:
  // stad, straat, dorp, landschap.
  bouwstapels: { x: 0, y: 571, stap: 40, breed: 36, hoog: 37 },
  // De laatste worp blijft links van de onderbalk staan, zoals in het origineel
  // (`catan-ref/shots/b07.png`): twee vakjes van 32×33 naast elkaar.
  worp: { x: 140, y: 730 },
  volgende: { x: 688, y: 724 },
  sluiten: { x: 800, y: 724 },
} as const;

export interface HudCallbacks {
  onStapelClick(index: number): void;
  onBouwstapelClick(soort: Bouwsoort): void;
  onHandClick(): void;
  onRuilClick(): void;
  onKaartClick(cardId: number): void;
  onVolgende(): void;
  onSluiten(): void;
}

/** De vier kaarten die je altijd kunt kopen. */
export type Bouwsoort = 'straat' | 'dorp' | 'stad' | 'landschap';

/** Wat het scherm over de kaarten in je hand moet weten. */
export interface HandKaart {
  readonly id: number;
  readonly naam: string;
  readonly speelbaar: boolean;
  /** Wat de kaart kost, als grondstofnaam → aantal. Voor de kostenstrook. */
  readonly kosten?: Readonly<Record<string, number | undefined>>;
}

export class Hud {
  readonly laag = new Container();
  /**
   * De laag voor vensters die over de HUD heen staan.
   *
   * `render()` veegt `laag` elke keer leeg — dat is precies de bedoeling voor de balken
   * en de stapels, maar dodelijk voor een geopend paneel. Een ruilscherm dat je opent en
   * daarna hertekent verdween zo spoorloos, terwijl de app dacht dat het nog openstond;
   * de kaartvraag aan het eind van je beurt bleef dan óók weg, want die wacht op een
   * leeg scherm. Vensters horen daarom in een eigen laag die nooit geleegd wordt.
   */
  readonly vensters = new Container();
  /** De gedeelde toelichting; het bord bezit hem, de HUD hangt er alleen aan. */
  private tooltips: Tooltips | undefined;
  private callbacks: HudCallbacks;

  /** Koppelt de toelichting van het bord, zodat er maar één kadertje bestaat. */
  zetTooltips(tooltips: Tooltips): void {
    this.tooltips = tooltips;
  }
  /** Klapt de hand open over het bord. */
  private handOpen = false;
  /** Welke bouwstapels je nu kunt betalen; de rest gaat onder de arcering. */
  private betaalbaar: ReadonlySet<Bouwsoort> = new Set();
  /** Mag je nu van een uitbouwstapel pakken? Zo niet, dan staan ze leeg. */
  private stapelsBruikbaar = true;
  /**
   * Staat er "je hebt te veel kaarten"? Dan opent de voorraad vanzelf, staat de stand
   * erbij (4/3) en knipperen de kaarten die je mag afleggen.
   */
  private afleggen: { stand: string } | undefined;
  /** Tikt op voor het knipperen; het bord tekent vaak genoeg opnieuw. */
  private knipper = 0;

  /** Zet het scherm in de afleg-stand, of haal het eruit. */
  zetAfleggen(stand: string | undefined): void {
    this.afleggen = stand === undefined ? undefined : { stand };
    if (stand !== undefined) this.handOpen = true;
  }

  /** De aanroeper weet of pakken nu mag; de HUD tekent het alleen. */
  zetStapelsBruikbaar(kan: boolean): void {
    this.stapelsBruikbaar = kan;
  }

  /**
   * Wijs je een stapel aan om een kaart onder te leggen?
   *
   * Dan moeten de stapels aanklikbaar zijn ook al mag je er niets uit pakken — het is de
   * tweede helft van het afleggen.
   */
  private kiestStapel = false;
  zetKiestStapel(kiest: boolean): void {
    this.kiestStapel = kiest;
  }

  /** Of de ruilknop aan de linkerrand nu iets doet. */
  private ruilbaar = false;
  zetRuilbaar(kan: boolean): void {
    this.ruilbaar = kan;
  }

  /** De aanroeper bepaalt wat betaalbaar is; de HUD tekent het alleen. */
  zetBetaalbaar(soorten: ReadonlySet<Bouwsoort>): void {
    this.betaalbaar = soorten;
  }

  /**
   * Welk figuur de speler van zichzelf heeft gemaakt.
   *
   * Los van zijn naam: je heet Speler1 en speelt Victoria. Daarom niet af te leiden uit
   * `view.you.name` — dat zou je naam en je portret aan elkaar vastknopen.
   */
  private spelerFiguur: string | undefined;
  zetSpelerFiguur(sleutel: string | undefined): void {
    this.spelerFiguur = sleutel;
  }

  /**
   * Welk figuur je tegenstander van zichzelf heeft gemaakt.
   *
   * Zonder dit kreeg iedereen online hetzelfde portret: `portret()` viel terug op de naam
   * en anders op Louis, want het figuur van de ander werd nooit over de lijn gestuurd.
   *
   * Let op bij het wisselen van potje: een blijven hangende waarde wint van naam-matching
   * en overschrijft dan het juiste portret van een computertegenstander. Hij hoort dus bij
   * elk nieuw potje gewist te worden.
   */
  private tegenstanderFiguur: string | undefined;
  zetTegenstanderFiguur(sleutel: string | undefined): void {
    this.tegenstanderFiguur = sleutel;
  }

  constructor(callbacks: HudCallbacks) {
    this.callbacks = callbacks;
  }

  isHandOpen(): boolean {
    return this.handOpen;
  }

  sluitHand(): void {
    this.handOpen = false;
  }

  /** Tekent de hele omlijsting opnieuw. */
  render(
    view: GameView,
    punten: { jij: number; tegen: number },
    laatsteMelding: string,
    hand: readonly HandKaart[] = [],
  ): void {
    this.laag.removeChildren();
    this.knipper += 1;

    this.statusbalk(view.opponent, punten.tegen, PLEK.balkBoven);
    this.statusbalk(view.you, punten.jij, PLEK.balkOnder);
    this.berichtenbalk(laatsteMelding);
    this.portret(
      view.opponent.name,
      PLEK.portretBoven,
      PLEK.naamplaatBoven,
      view,
      view.opponent.seat,
      this.tegenstanderFiguur,
    );
    this.portret(
      view.you.name,
      PLEK.portretOnder,
      PLEK.naamplaatOnder,
      view,
      view.you.seat,
      this.spelerFiguur,
    );
    // Het origineel toont óf de uitbouwstapels (als je mag pakken) óf de bouwstapels
    // (als je mag bouwen) — nooit allebei. Zie `catan-ref/shots/mij/`: in de aanvulfase
    // staan de vijf genummerde stapels er, in de hoofdfase de vier bouwvakjes.
    if (this.stapelsBruikbaar || this.kiestStapel) this.uitbouwstapels(view);
    else this.bouwstapels(this.betaalbaar);
    this.ruilknop();
    this.handstapel(view.opponent.handCount, PLEK.handstapelBoven, false);
    this.handstapel(view.you.hand.length, PLEK.handstapelOnder, true);
    this.laatsteWorp(view);
    this.onderknoppen(view);
    if (this.handOpen) this.toonHand(hand);
  }

  /**
   * De kaartpockets: je hand klein naast je kaartvoorraad.
   *
   * Zo deed het origineel het (zie `catan-ref/shots/b03b.png`): klik je op de stapel aan
   * de rechterrand, dan schuift daar een paneel uit met je kaarten naast elkaar — het
   * plaatje boven, een naamplaatje eronder. Het paneel schuift open vanaf de stapel, het
   * verschijnt er niet zomaar.
   *
   * Er is geen bladermechaniek nodig: de handlimiet is drie en groeit hooguit naar zes,
   * dus alles past altijd op één rij.
   */
  private toonHand(hand: readonly HandKaart[]): void {
    if (hand.length === 0) return;

    const cel = 92;
    const kier = 4;
    const plaatHoog = 68;
    const naamHoog = 20;
    const marge = 10;
    // Onder de kaarten loopt in het origineel een bladerbalk met een pijl aan elke kant
    // (`catan-ref/shots/ref/34-pockets-open.png`). Onze hand past altijd op één rij —
    // de limiet is drie en groeit hooguit naar zes — dus de pijlen doen niets. Ze staan
    // er wel: zonder die balk oogt het paneel als een ander paneel dan dat van de cd.
    const balkHoog = 20;
    const breed = Math.max(hand.length * (cel + kier) - kier + marge * 2, 240);
    const hoog = plaatHoog + naamHoog + marge * 2 + balkHoog;

    // Rechts uitgelijnd op de kaartvoorraad, zodat het paneel er zichtbaar uit komt.
    const rechts = PLEK.handstapelOnder.x - 6;
    const doelX = rechts - breed;
    const y = PLEK.handstapelOnder.y - 4;

    const doos = new Container();
    doos.y = y;
    doos.addChild(negenStuk(SKIN.paneelGroot, breed, hoog, 14));

    for (const [i, kaart] of hand.entries()) {
      const vak = new Container();
      vak.x = marge + i * (cel + kier);
      vak.y = marge;

      if (hasAsset('kaarten', String(kaart.id))) {
        const plaat = new Sprite(Texture.from(asset('kaarten', String(kaart.id)).src));
        plaat.setSize(cel, plaatHoog);
        vak.addChild(plaat);
      }

      // De kostenstrook linksboven, zoals op de gedrukte kaart: één rij per grondstof-
      // soort, het aantal naast elkaar in die rij, in de gedrukte volgorde. Alles staat
      // erop — een strook die afkapt vertelt de speler de verkeerde prijs, en zo ontstond
      // "ik moest wol betalen terwijl dat niet op de kaart staat": IJzeren Hein kost
      // graan 2, erts 2, wol 2, en de oude strook kapte na vier iconen precies de wol af.
      for (const [rij, { soort, aantal }] of kostenRijen(kaart.kosten).entries()) {
        if (!hasAsset('symbolen', soort)) continue;
        for (let k = 0; k < aantal; k++) {
          const icoon = new Sprite(Texture.from(asset('symbolen', soort).src));
          icoon.setSize(KOSTEN_ICOON.maat, KOSTEN_ICOON.maat);
          icoon.x = 2 + k * KOSTEN_ICOON.stap;
          icoon.y = 2 + rij * KOSTEN_ICOON.stap;
          vak.addChild(icoon);
        }
      }

      // Het naamplaatje eronder. Het origineel kleurt dat van een speelbare kaart groen;
      // er is geen apart groen plaatje, het is dezelfde plaquette met een tint erover.
      const plaquette = negenStuk(SKIN.plaquette, cel, naamHoog, 6);
      if (kaart.speelbaar) {
        const kern = plaquette.children[0];
        if (kern !== undefined && 'tint' in kern) {
          (kern as { tint: number }).tint = 0x8fdc6a;
        }
      }
      plaquette.y = plaatHoog;
      vak.addChild(plaquette);

      const naam = tekst(kaart.naam, 11);
      naam.anchor.set(0.5, 0.5);
      naam.x = cel / 2;
      naam.y = plaatHoog + naamHoog / 2;
      vak.addChild(naam);

      // Moet je afleggen, dan knipperen juist de kaarten die weg mogen — en verder is
      // alles onaanklikbaar. Zo wijst het scherm zelf de weg.
      const magWeg = this.afleggen !== undefined;
      if (magWeg) {
        vak.alpha = 0.45 + 0.55 * Math.abs(Math.sin(this.knipper / 6));
        vak.eventMode = 'static';
        vak.cursor = 'pointer';
        vak.on('pointertap', () => {
          klik();
          this.callbacks.onKaartClick(kaart.id);
        });
      } else if (kaart.speelbaar) {
        vak.eventMode = 'static';
        vak.cursor = 'pointer';
        vak.on('pointertap', () => {
          klik();
          this.callbacks.onKaartClick(kaart.id);
        });
        // Optillen bij aanwijzen — zo zie je welke kaart je zou spelen.
        vak.on('pointerover', () => (vak.y = marge - 6));
        vak.on('pointerout', () => (vak.y = marge));
      } else {
        // Wel doffer, maar nog goed te lezen: je moet kunnen zien wélke kaart je niet
        // kunt spelen, anders zegt het je niets.
        vak.alpha = 0.75;
      }
      doos.addChild(vak);
    }

    // De stand erboven: "4/3" — zoveel kaarten heb je, zoveel mag je er hebben.
    if (this.afleggen !== undefined) {
      const stand = tekst(`${this.afleggen.stand} — leg er een af`, 14, 0xffb0b0);
      stand.anchor.set(0.5, 1);
      stand.x = breed / 2;
      stand.y = -4;
      doos.addChild(stand);
    }

    this.bladerbalk(doos, marge, hoog - balkHoog - 4, breed - marge * 2, balkHoog);
    this.laag.addChild(doos);

    // Uitschuiven vanaf de stapel. Wie verminderde beweging aan heeft, krijgt het paneel
    // meteen op zijn plek.
    const stil = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (stil) {
      doos.x = doelX;
      return;
    }
    doos.x = rechts;
    const begonnen = performance.now();
    const duur = 260;
    const schuif = (): void => {
      const t = Math.min(1, (performance.now() - begonnen) / duur);
      // Zachte uitloop: snel weg van de stapel, rustig op zijn plek.
      const e = 1 - (1 - t) * (1 - t);
      doos.x = rechts + (doelX - rechts) * e;
      if (t < 1 && !doos.destroyed) window.requestAnimationFrame(schuif);
    };
    window.requestAnimationFrame(schuif);
  }

  private statusbalk(
    speler: GameView['you'] | GameView['opponent'],
    punten: number,
    plek: { x: number; y: number },
  ): void {
    const doos = new Container();
    doos.x = plek.x;
    doos.y = plek.y;
    doos.addChild(skinSprite(SKIN.statusbalkTien));

    // Tel de grondstoffen die op de landschappen liggen.
    const voorraad = new Map<string, number>();
    for (const tile of speler.principality.landscapes) {
      const soort = LANDSCAPE_RESOURCE[tile.landscape];
      voorraad.set(soort, (voorraad.get(soort) ?? 0) + tile.resources);
    }

    for (const [i, naam] of BALK_SYMBOLEN.entries()) {
      const waarde =
        naam === 'overwinning'
          ? String(punten)
          : naam === 'riddermacht'
            ? // De handschoen laat zien hóe sterk je riddermacht is (handboek p. 11); dat
              // je de figuur hebt, zie je aan het beeldje onder je naam. Er stond een
              // sterretje, en daarmee kon je je eigen sterkte niet aflezen.
              String(speler.strength)
            : naam === 'handelsmacht'
              ? String(speler.commercePoints)
              : naam === 'toernooi'
                ? String(speler.tournamentPoints)
                : String(voorraad.get(naam) ?? 0);

      const label = tekst(waarde, 15);
      label.anchor.set(0.5, 0.5);
      // Het getal staat rechts van het icoon, binnen hetzelfde vakje.
      label.x = (VAK_X[i] ?? 0) + 33;
      label.y = 20;
      doos.addChild(label);

      // Elk vakje krijgt zijn naam, want de iconen zijn klein en niet allemaal duidelijk
      // — de molen voor handelsmacht ziet er bijvoorbeeld uit als gekruiste lansen.
      if (this.tooltips !== undefined) {
        const vak = new Container();
        const vlak = new Graphics();
        vlak.rect(0, 0, 45, 40).fill({ color: 0xffffff, alpha: 0.001 });
        vak.addChild(vlak);
        vak.x = VAK_X[i] ?? 0;
        volgTooltip(vak, this.tooltips, BALK_UITLEG[naam] ?? naam, () => ({
          x: doos.x + (VAK_X[i] ?? 0),
          y: doos.y + 40,
        }));
        doos.addChild(vak);
      }
    }
    this.laag.addChild(doos);
  }

  private berichtenbalk(melding: string): void {
    if (melding === '') return;
    const doos = new Container();
    doos.x = PLEK.berichten.x;
    doos.y = PLEK.berichten.y;
    doos.addChild(skinSprite(SKIN.berichtenbalk));
    const label = tekst(melding, 14, 0xffe9b0);
    label.anchor.set(0.5, 0.5);
    label.x = 330;
    label.y = 14;
    doos.addChild(label);
    this.laag.addChild(doos);
  }

  private portret(
    naam: string,
    plek: { x: number; y: number },
    naamPlek: { x: number; y: number },
    view: GameView,
    seat: Seat,
    figuur?: string,
  ): void {
    // Het portret zelf, op ware grootte en rechts uitgelijnd tegen de schermrand. Een
    // gekozen figuur gaat voor; anders staat de naam van de speler er meestal wel bij.
    const gekozen = figuur?.toLowerCase();
    const sleutel =
      gekozen !== undefined && hasAsset('portretten', gekozen)
        ? gekozen
        : hasAsset('portretten', naam.toLowerCase())
          ? naam.toLowerCase()
          : SPELER_PORTRET;
    if (hasAsset('portretten', sleutel)) {
      const bron = asset('portretten', sleutel);
      const beeld = new Sprite(Texture.from(bron.src));
      beeld.x = RASTER_BREED - bron.width - 2;
      beeld.y = plek.y;
      this.laag.addChild(beeld);
    }

    // De naamplaat met de twee vakjes voor de speelfiguren.
    const plaat = new Container();
    plaat.x = naamPlek.x;
    plaat.y = naamPlek.y;
    plaat.addChild(skinSprite(SKIN.naamplaat));
    const label = tekst(naam, 13);
    label.anchor.set(0.5, 0);
    label.x = 65;
    label.y = 1;
    plaat.addChild(label);

    // Ridderfiguur links, molenfiguur rechts — de vakjes in het plaatje.
    if (view.knightToken === seat) plaat.addChild(this.figuur('riddermacht', 14, 20));
    if (view.windmillToken === seat) plaat.addChild(this.figuur('handelsmacht', 76, 20));
    if (this.tooltips !== undefined) {
      const speler = seat === view.you.seat ? view.you : view.opponent;
      volgTooltip(
        plaat,
        this.tooltips,
        `${naam} — ${speler.strength} sterkte, ${speler.commercePoints} handelspunten`,
        () => ({ x: naamPlek.x - 120, y: naamPlek.y }),
      );
    }
    this.laag.addChild(plaat);
  }
  /**
   * De laatste worp, blijvend in beeld naast de onderbalk.
   *
   * De animatie is voorbij voor je hem goed hebt gezien; het origineel laat daarom de
   * twee vlakken staan tot de volgende worp. Zo kun je altijd nazoeken wat er viel.
   */
  private laatsteWorp(view: GameView): void {
    const worp = view.dice;
    if (worp === undefined) return;

    const doos = new Container();
    doos.x = PLEK.worp.x;
    doos.y = PLEK.worp.y;

    const vlak = (groep: string, naam: string, x: number): void => {
      if (!hasAsset(groep, naam)) return;
      const beeld = new Sprite(Texture.from(asset(groep, naam).src));
      beeld.setSize(32, 33);
      beeld.x = x;
      doos.addChild(beeld);
    };
    // Eén vlak, zoals het origineel: de opbrengststeen. Wat de gebeurtenissteen opleverde
    // vertelt de banner die er meteen na de worp overheen komt.
    vlak('dobbelstenen', `opbrengst-${worp.production}`, 0);
    this.laag.addChild(doos);
  }

  private figuur(symbool: string, x: number, y: number): Sprite {
    const beeld = new Sprite(Texture.from(asset('symbolen', symbool).src));
    beeld.setSize(24, 24);
    beeld.x = x;
    beeld.y = y;
    return beeld;
  }

  /**
   * De bladerbalk onder een kaartpaneel.
   *
   * Een langwerpig vak met aan beide uiteinden een pijlknop, zoals het origineel hem
   * onder de kaartbrowser en de kaartpockets zet.
   */
  private bladerbalk(ouder: Container, x: number, y: number, breed: number, hoog: number): void {
    const balk = new Container();
    balk.x = x;
    balk.y = y;
    balk.addChild(negenStuk(SKIN.paneelSmal, breed, hoog, 6));

    // De pijlen zijn eigen plaatjes in de skin (50×19), niet getekende tekens.
    const pijl = (naam: string, px: number): void => {
      if (!hasAsset('skin', naam)) return;
      const beeld = new Sprite(Texture.from(asset('skin', naam).src));
      beeld.setSize(44, 17);
      beeld.x = px;
      beeld.y = (hoog - 17) / 2;
      beeld.alpha = 0.7;
      balk.addChild(beeld);
    };
    pijl(SKIN.pijlLinks, 4);
    pijl(SKIN.pijlRechts, breed - 48);
    ouder.addChild(balk);
  }

  /**
   * De vier bouwstapels: straat, dorp, stad en landschap.
   *
   * Deze horen zichtbaar te zijn *voordat* je wilt bouwen, want juist daaraan zie je of
   * het kan. Kun je betalen, dan staat de kaart in kleur; kun je dat niet, dan gaat de
   * diagonale arcering uit de originele skin (`MENU_142`, letterlijk het "kan nu niet"-
   * plaatje) eroverheen.
   */
  private bouwstapels(betaalbaar: ReadonlySet<Bouwsoort>): void {
    // De volgorde van het origineel, van boven naar beneden.
    //
    // Als gezicht van de stapel nemen we de **tegelplaat** (`bouwwerken`), niet de
    // kaartplaat: een kaart van 400×400 met rand en tekst wordt op dertig pixels een
    // mozaïekje, terwijl het huisje of het wegdek meteen herkenbaar is — en dat is ook
    // wat er in het origineel staat.
    const soorten: readonly { soort: Bouwsoort; groep: string; plaat: string }[] = [
      { soort: 'stad', groep: 'bouwwerken', plaat: 'stad' },
      { soort: 'straat', groep: 'bouwwerken', plaat: 'straat' },
      { soort: 'dorp', groep: 'bouwwerken', plaat: 'dorp' },
      { soort: 'landschap', groep: 'landschappen', plaat: 'weide' },
    ];

    for (const [i, item] of soorten.entries()) {
      const { breed, hoog } = PLEK.bouwstapels;
      const doos = new Container();
      doos.x = PLEK.bouwstapels.x;
      doos.y = PLEK.bouwstapels.y + i * PLEK.bouwstapels.stap;

      // Het vakje zelf: een lichte rand met de plaat erin, zoals de stapels links.
      doos.addChild(negenStuk(SKIN.stapel, breed, hoog, 4));
      if (hasAsset(item.groep, item.plaat)) {
        const plaat = new Sprite(Texture.from(asset(item.groep, item.plaat).src));
        plaat.setSize(breed - 6, hoog - 6);
        plaat.x = 3;
        plaat.y = 3;
        doos.addChild(plaat);
      }

      // Kun je niet betalen, dan gaat de diagonale arcering eroverheen — precies het
      // "kan nu niet"-plaatje dat het origineel hier gebruikt.
      const kan = betaalbaar.has(item.soort);
      if (!kan) {
        const grijs = arcering(breed - 6, hoog - 6);
        grijs.x = 3;
        grijs.y = 3;
        doos.addChild(grijs);
      }

      if (this.tooltips !== undefined) {
        const label =
          item.soort === 'landschap'
            ? 'Landschapskaarten'
            : item.soort.charAt(0).toUpperCase() + item.soort.slice(1);
        volgTooltip(doos, this.tooltips, label, () => ({ x: doos.x + breed, y: doos.y }));
      }

      if (kan) {
        doos.eventMode = 'static';
        doos.cursor = 'pointer';
        doos.on('pointertap', () => {
          klik();
          this.callbacks.onBouwstapelClick(item.soort);
        });
      }
      this.laag.addChild(doos);
    }
  }

  /**
   * De ruilknop aan de linkerrand.
   *
   * Het handboek wijst hem expliciet aan als de ingang naar het ruilmenu (p. 14); zonder
   * die knop is ruilen alleen te vinden als het toevallig al mag.
   */
  private ruilknop(): void {
    const doos = new Container();
    doos.x = PLEK.ruilknop.x;
    doos.y = PLEK.ruilknop.y;
    doos.addChild(skinSprite(this.ruilbaar ? SKIN.ruilknopAan : SKIN.ruilknop));
    if (this.ruilbaar) {
      doos.eventMode = 'static';
      doos.cursor = 'pointer';
      doos.on('pointertap', () => {
        klik();
        this.callbacks.onRuilClick();
      });
    }
    if (this.tooltips !== undefined) {
      volgTooltip(
        doos,
        this.tooltips,
        this.ruilbaar ? 'Ruilen — met de bank of met je tegenstander' : 'Ruilen kan nu niet',
        () => ({ x: doos.x + 44, y: doos.y + 30 }),
      );
    }
    this.laag.addChild(doos);
  }

  /** De vijf uitbouwstapels aan de linkerrand, met hun aantal erop. */
  private uitbouwstapels(view: GameView): void {
    for (const [i, aantal] of view.stackSizes.entries()) {
      const doos = new Container();
      doos.x = PLEK.stapels.x;
      doos.y = PLEK.stapels.y + i * PLEK.stapels.stap;

      // Een stapel met kaarten erin is voortaan het hele potje zichtbaar, mét zijn aantal.
      // Alleen *aanklikbaar* varieert.
      //
      // Hiervoor werd een onbruikbare stapel als leeg vak getekend — geen plaatje, geen
      // getal. Dat leek op het origineel, maar het maakte de Bouwmeester onspeelbaar: juist
      // tijdens die gebeurtenis mag je uit een stapel kiezen, en juist dan waren ze
      // onzichtbaar. Het was ook de verdachte in de eerdere klacht "ik zie de stapels niet".
      // Een lege stapel blijft een leeg vak; daar valt niets te kiezen.
      const heeftKaarten = aantal > 0;
      const aanklikbaar = heeftKaarten && (this.stapelsBruikbaar || this.kiestStapel);
      doos.addChild(skinSprite(heeftKaarten ? SKIN.stapel : SKIN.leegVak));

      if (heeftKaarten) {
        const label = tekst(String(aantal), 15);
        label.anchor.set(0.5, 0.5);
        label.x = 20;
        label.y = 20;
        // Een stapel waar je nu niets uit mag halen staat er gedempt bij: je ziet hem
        // liggen, maar hij dringt zich niet op.
        label.alpha = aanklikbaar ? 1 : 0.55;
        doos.addChild(label);
      }
      if (!aanklikbaar) doos.alpha = 0.6;

      if (aanklikbaar) {
        doos.eventMode = 'static';
        doos.cursor = 'pointer';
        doos.on('pointertap', () => {
          klik();
          this.callbacks.onStapelClick(i);
        });
      }

      if (this.tooltips !== undefined) {
        volgTooltip(
          doos,
          this.tooltips,
          heeftKaarten ? `Uitbouwkaartenstapel — ${aantal} kaarten` : 'Uitbouwkaartenstapel (leeg)',
          () => ({ x: doos.x + 40, y: doos.y }),
        );
      }
      this.laag.addChild(doos);
    }

    // De gebeurteniskaartenstapel eronder.
    const geb = new Container();
    geb.x = PLEK.stapels.x;
    geb.y = PLEK.stapels.y + view.stackSizes.length * PLEK.stapels.stap + 8;
    geb.addChild(skinSprite(SKIN.gebeurtenisstapel));
    const label = tekst(String(view.eventDeckSize), 15);
    label.anchor.set(0.5, 0.5);
    label.x = 20;
    label.y = 20;
    geb.addChild(label);
    this.laag.addChild(geb);
  }

  /** De handkaarten als stapel aan de rechterrand, met het aantal eronder. */
  private handstapel(aantal: number, plek: { x: number; y: number }, vanJou: boolean): void {
    const doos = new Container();
    doos.x = plek.x;
    doos.y = plek.y;
    doos.addChild(skinSprite(aantal === 0 ? SKIN.handstapelLeeg : SKIN.handstapel));

    const label = tekst(String(aantal), 15);
    label.anchor.set(0.5, 0.5);
    label.x = 24;
    label.y = 110;
    doos.addChild(label);

    if (vanJou && aantal > 0) {
      doos.eventMode = 'static';
      doos.cursor = 'pointer';
      doos.on('pointertap', () => {
      klik();
        this.handOpen = !this.handOpen;
        this.callbacks.onHandClick();
      });
    }
    this.laag.addChild(doos);
  }

  private onderknoppen(view: GameView): void {
    const volgende = knop({
      soort: 'volgende',
      onClick: () => this.callbacks.onVolgende(),
      uitgeschakeld: !view.yourTurn,
    });
    volgende.x = PLEK.volgende.x;
    volgende.y = PLEK.volgende.y;
    this.laag.addChild(volgende);

    const sluiten = knop({ soort: 'sluiten', onClick: () => this.callbacks.onSluiten() });
    sluiten.x = PLEK.sluiten.x;
    sluiten.y = PLEK.sluiten.y;
    this.laag.addChild(sluiten);
  }
}

export { RASTER_BREED, RASTER_HOOG };
